import { useCallback, useRef, useState } from "react";
import { useMutation } from "urql";

import { ExportFileFormat } from "@/components/graphql/schema.graphql.d";
import { useSnackbar } from "@/components/ui-v2/Snackbar";
import { ExportFiles } from "@/graphql/files.graphql";
import { useBasicData } from "@/hooks";
import { useUrqlResponseHandler } from "@/hooks/useUrqlResponseHandler";
import { ObjectType } from "@/types";
import { buildExportDownloadName, downloadFromURL } from "@/utils/download";
import { getObjectType } from "@/utils/libraryObjectUtils";

import {
  ExportFormState,
  ExportModality,
  PNG_SEQUENCE_FORMAT,
} from "../components/ExportDialog/components/ExportForm";
import { SelectedObject } from "../components/selection-sidebar/types";
import { BasicErrorFragment } from "./queries.graphql";

type ExportObjectsData = Pick<
  ExportFormState,
  | "compression"
  | "exportSize"
  | "format"
  | "fps"
  | "frameExtractionFps"
  | "removeBackground"
  | "videoCodec"
>;

export const useExportObjects = () => {
  const { getResponseFromState } = useUrqlResponseHandler();
  const { showError } = useSnackbar();
  const { workspace } = useBasicData();

  const [, exportFilesMutation] = useMutation(ExportFiles);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const handleError = useCallback(
    (error: BasicErrorFragment) => {
      if (error?.code === "EXPORT_FILES_FAILED") {
        showError(error.message);
      } else {
        showError("Failed to export files");
      }
    },
    [showError],
  );

  const exportObjects = useCallback(
    async (objects: SelectedObject[], formState?: ExportObjectsData) => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      try {
        // Extract files from selected objects (files have __typename "File").
        // Names drive the download filename, ids the mutation.
        const files = objects.filter((obj) => obj.__typename === "File");
        const fileIds = files.map((obj) => obj.id);

        if (fileIds.length === 0) {
          showError("No files selected for export");
          return null;
        }

        if (!workspace?.id) {
          showError("No workspace selected");
          return null;
        }

        const isGif = formState?.format === "GIF";
        const isPngSequence = formState?.format === PNG_SEQUENCE_FORMAT;
        const isMp4OrMov =
          formState?.format === "MP4" || formState?.format === "MOV";
        const isWebm = formState?.format === "WEBM";

        const getFormatOptions = () => {
          if (isPngSequence) {
            return {
              extractFrames: true,
              frameExtractionFps: formState?.frameExtractionFps,
            };
          }
          if (isGif) {
            return { fps: formState?.fps };
          }
          // Keyed on the requested format rather than on whether a codec was chosen, so
          // leaving codec and compression unset stays a video export. Sending neither is
          // what lets the server carry the source's encoding across untouched; WEBM is
          // always VP9, so it takes compression alone.
          if (isMp4OrMov || isWebm) {
            return {
              videoCodec: isMp4OrMov ? formState?.videoCodec : undefined,
              compression: formState?.compression,
            };
          }
          return {
            removeBackground: formState?.removeBackground,
            size: formState?.exportSize,
          };
        };

        const mutationResult = await exportFilesMutation({
          input: {
            workspaceId: workspace.id,
            fileIds,
            options: {
              ...(formState
                ? {
                    format: isPngSequence
                      ? ("PNG" as ExportFileFormat)
                      : (formState.format as ExportFileFormat),
                    ...getFormatOptions(),
                  }
                : {}),
            },
          },
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const { data, error } = getResponseFromState({
          state: mutationResult,
          errorMessage: "Failed to export files",
        });

        if (error) {
          handleError(error);
          return;
        }

        if (data?.exportFiles?.__typename !== "Link" || !data.exportFiles.url) {
          showError("Failed to export files");
          return;
        }

        // Trigger browser download
        const url = data.exportFiles.url;
        const name = buildExportDownloadName(
          files.map((file) => file.name),
          url,
        );

        return await downloadFromURL([{ name, url }]);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      exportFilesMutation,
      workspace?.id,
      getResponseFromState,
      handleError,
      showError,
    ],
  );

  const cancelExport = useCallback(() => {
    requestIdRef.current++;
    setIsLoading(false);
  }, []);

  const canExportObjects = useCallback((objects: SelectedObject[]): boolean => {
    const types = new Set(objects.map((object) => getObjectType(object)));
    const allowedTypes: ObjectType[] = ["image", "video"];
    return (
      objects.length > 0 &&
      types.size === 1 &&
      allowedTypes.some((t) => types.has(t))
    );
  }, []);

  const getExportModality = useCallback(
    (objects: SelectedObject[]): ExportModality => {
      const types = new Set(objects.map((object) => getObjectType(object)));
      if (objects.length === 0 || types.size !== 1) return null;
      if (types.has("image")) return "image";
      if (types.has("video")) return "video";
      return null;
    },
    [],
  );

  return {
    cancelExport,
    canExportObjects,
    exportObjects,
    getExportModality,
    isLoading,
  };
};
